import { z } from "zod";
import { BaseFirestoreWStatusTagsSchema } from "./firestore.schema";
import { DocumentSchema, PublicDocumentSchema } from "./document.schema";
import { DateTypeSchema, TemplateTypeSchema } from "./core.schema";

// Schemas

export const ApproverTypeSchema = z.enum(["team", "user", "none"]);

export const VoteAnswerSchema = z.enum(["yes", "no", "abstain"]);

export const VoteTypeSchema = z.enum(["voice", "hand", "rollcall", "ballot"]);

export const ReviewStatusSchema = z.enum([
  "rejected",
  "pending",
  "approved",
  "deferred",
]);

export const DocumentApprovalTypeSchema = z.enum([
  "proclamations",
  "resolutions",
  "ordinances",
  "agreements",
  "iga",
  "report",
  "plan",
  "appointment",
]);

export const ApproverSchema = z.object({
  approverType: ApproverTypeSchema.optional(),
  approverId: z.string(),
});

export const PresenterSchema = z.object({
  userId: z.string().optional(),
  manualEntry: z.string().optional(),
});

export const AgendaItemUserVoteSchema = z.object({
  userId: z.string(),
  vote: VoteAnswerSchema,
  reason: z.string().optional(),
});

export const AgendaItemVotesSchema = z.object({
  type: VoteTypeSchema,
  yes: z.number(),
  no: z.number(),
  abstain: z.number(),
  users: AgendaItemUserVoteSchema.array().optional(),
});

export const AgendaItemPositionTypeSchema = z.enum([
  "afterItem",
  "atSectionStart",
]);

export const AgendaItemPositionSchema = z.object({
  positionType: AgendaItemPositionTypeSchema,
  positionId: z.string(),
});

export const AgendaItemTemplateSchema: z.ZodType<{
  partialAgendaItem?: Partial<z.infer<typeof AgendaItemSchema>>;
  position?: z.infer<typeof AgendaItemPositionSchema>;
}> = z.object({
  partialAgendaItem: z.lazy(() => AgendaItemSchema.partial()).optional(),
  createdAgendaItemId: z.string().optional(),
  position: AgendaItemPositionSchema.optional(),
});

const BaseAgendaItemMotionSchema = z.object({
  motionId: z.string(),
  timestamp: z.number(),
  timestampEnd: z.number().optional(),
  moved: z.string().optional(),
  seconded: z.string().optional(),
  debate: z.string().optional(),
  comments: z.string().optional(),
  amendment: z.string().optional(),
  votes: AgendaItemVotesSchema.optional(),
  status: ReviewStatusSchema.optional(),
  resolved: z.boolean().optional(),
  agendaItemTemplate: AgendaItemTemplateSchema.optional(),
});

type BaseAgendaItemMotion = z.infer<typeof BaseAgendaItemMotionSchema> & {
  nesting?: BaseAgendaItemMotion[];
};

export const AgendaItemMotionSchema: z.ZodType<BaseAgendaItemMotion> =
  BaseAgendaItemMotionSchema.extend({
    nesting: z.lazy(() => AgendaItemMotionSchema.array()).optional(),
  });

export const AgendaItemReviewSchema = z.object({
  meetingId: z.string().optional(),
  timestamp: z.number().optional(),
  timestampEnd: z.number().optional(),
  originalMotion: z.string().optional(),
  reviewedBy: z.string().optional(),
  status: ReviewStatusSchema.optional(),
  comments: z.string().optional(),
  moved: z.string().optional(),
  seconded: z.string().optional(),
  votes: AgendaItemVotesSchema.optional(),
  motions: AgendaItemMotionSchema.array().optional(),
});

export const AgendaItemSchema = BaseFirestoreWStatusTagsSchema.merge(
  TemplateTypeSchema
)
  .extend({
    name: z.string(),
    description: z.string(),
    body: z.string(),
    submittedBy: z.string(),
    type: z.string(),
    documentIds: z.string().array(),
    documents: DocumentSchema.array().nullable(),
    parentIds: z.string().array().optional(),
    teamId: z.string().optional(),
    review: AgendaItemReviewSchema.nullable().optional(),
    publishedAt: DateTypeSchema.optional(),
    newVersionOfTheItemId: z.string().optional(),
  })
  .extend({
    // These schema properties are managed in the database.
    // Update these when making changes in /api/scripts/seed/config/agendaItemMetadata.ts
    vendor: z.string().optional(),
    why: z.string().optional(),
    stateApprovedPricing: z.boolean().optional(),
    prevailingWage: z.boolean().optional(),
    emergencyOverride: z.boolean().optional(),
    biddingRequired: z.boolean().optional(),
    biddingThresholds: z.string().optional(),
    requestedApprovalDate: DateTypeSchema.optional(),
    price: z.string().optional(),
    annualPrice: z.string().optional(),
    term: z.string().optional(),
    docapprovaltype: DocumentApprovalTypeSchema.optional(),
    lease: z.boolean().optional(),
    software: z.boolean().optional(),
    approver: ApproverSchema.optional(),
    presenter: PresenterSchema.or(z.string()).optional(),
    comment: z.string().optional(),
    script: z.string().optional(),
    motion: z.string().optional(),
    expires: z.boolean().optional(),
    expirationDate: DateTypeSchema.optional(),
    expirationNotification: z.string().array().optional(),
    glAccount: z.string().optional(),
  });

export const PublicAgendaItemSchema = AgendaItemSchema.pick({
  // --------------------
  // BaseFirestoreSchema:
  // _deleted: true,
  _version: true,
  // _preserveVersion: true,
  // _schemaVersion: true,
  // _source: true,
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,

  // -------------------------------
  // BaseFirestoreWStatusTagsSchema:
  // status: true,
  // tags: true,

  // -------------------
  // TemplateTypeSchema:
  // template: true,
  // templateId: true,

  // -----------------------------
  // base AgendaItemSchema fields:
  name: true,
  description: true,
  body: true,
  submittedBy: true,
  type: true,
  documentIds: true,
  documents: true,
  // parentIds: true,
  teamId: true,
  review: true,
  publishedAt: true,
  // newVersionOfTheItemId: true,

  // -----------------------------------
  // additional AgendaItemSchema fields:
  vendor: true,
  why: true,
  stateApprovedPricing: true,
  prevailingWage: true,
  emergencyOverride: true,
  biddingRequired: true,
  biddingThresholds: true,
  requestedApprovalDate: true,
  price: true,
  annualPrice: true,
  term: true,
  docapprovaltype: true,
  lease: true,
  software: true,
  approver: true,
  presenter: true,
  comment: true,
  script: true,
  motion: true,
  expires: true,
  expirationDate: true,
  expirationNotification: true,
  glAccount: true,
}).extend({
  documents: PublicDocumentSchema.array().nullable(),
});

// DTOs

export const CreateAgendaItemDtoSchema = AgendaItemSchema.partial();

export const PutAgendaItemDtoSchema = CreateAgendaItemDtoSchema;

export const PatchAgendaItemDtoSchema = CreateAgendaItemDtoSchema;
